import { ApolloClient, from, HttpLink, InMemoryCache, NormalizedCacheObject, split } from '@apollo/client';
import { RetryLink } from '@apollo/client/link/retry';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { getMainDefinition } from '@apollo/client/utilities';
import { createClient } from 'graphql-ws';
import { createPersistedQueryLink } from '@apollo/client/link/persisted-queries';
import { sha256 } from 'crypto-hash';
import { createErrorLink } from './errorHandler';

interface LocalApolloClientOptions {
  fetch: WindowOrWorkerGlobalScope['fetch'];
}

let client: ApolloClient<NormalizedCacheObject> | null = null;

export function getClient(): ApolloClient<NormalizedCacheObject> {
  if (!client) {
    throw new Error('Apollo Client aún no ha sido inicializado.');
  }
  return client;
}

export function setClient(newClient: ApolloClient<NormalizedCacheObject>) {
  client = newClient;
}

export const newApolloClient = ({ fetch }: LocalApolloClientOptions): ApolloClient<NormalizedCacheObject> => {
  const httpLinkInstance = httpLink(fetch);
  const wsLink = new GraphQLWsLink(
    createClient({
      url: import.meta.env.VITE_WS_URL || 'ws://localhost:3000/subscriptions',
    }),
  );

  const link = from([
    createPersistedQueryLink({ sha256 }),
    createErrorLink(),
    retryLink(),
    split(
      ({ query }) => {
        const definition = getMainDefinition(query);
        return definition.kind === 'OperationDefinition' && definition.operation === 'subscription';
      },
      wsLink,
      httpLinkInstance,
    ),
  ]);

  return new ApolloClient({
    link,
    cache: new InMemoryCache({
      typePolicies: {
        Product: {
          keyFields: false,
        },
      },
    }),
  });
};

const retryLink = () => {
  return new RetryLink({
    delay: {
      initial: 300,
      max: Infinity,
      jitter: true,
    },
    attempts: {
      max: 5,
      retryIf: (error) => !!error && error.statusCode !== 419,
    },
  });
};

const httpLink = (newFetch: WindowOrWorkerGlobalScope['fetch']) => {
  return new HttpLink({
    uri: import.meta.env.VITE_API_URL,
    fetch: newFetch,
    headers: {
      'X-Client-Tag': import.meta.env.VITE_TAG_VERSION || 'dev',
    },
  });
};
